import React from "react";
import { useTranslation } from "react-i18next";
import transactionCurrencyCodeOptions from "../../../../../utils/transactionCurrencyCodeOptions/TransactionCurrencyCodeOptions";
import { FormBuilder, formBuilderParams } from "components/FormBuilder/FormBuilder";
import { useWatch } from "react-hook-form";

const PAGE_LOCALE_KEY = "page.employees.";
const FORM_LABEL_KEY = `${PAGE_LOCALE_KEY}employeeDetail.config.fields`;
const optQrcodeScanModeOptions = [
  { title: "Customer scan mode", value: "0", label: "Customer scan mode" },
  { title: "Merchant scan mode", value: "1", label: "Merchant scan mode" },
  { title: "Pass QR code to POS", value: "2", label: "Both scan mode" },
];
const dccCurSelModeOptions = [
  {
    title: "Forcibly use local currency",
    value: "LOCAL",
    label: "Forcibly use local currency",
  },
  {
    title: "Forcibly use foreign currency",
    value: "FOREIGN",
    label: "Forcibly use foreign currency",
  },
  { title: "Ask for choice", value: "ASK", label: "Ask for choice" },
];

const EmployeeAdvancedConfig = ({ formProps }) => {
  const { t } = useTranslation();

  const optTipIndicator = useWatch({ control: formProps.control, name: "optTipIndicator" });
  const sttEnable = useWatch({ control: formProps.control, name: "sttEnable", });

  const currencyOptions = transactionCurrencyCodeOptions([
    "hkd",
    "sgd",
    "php",
    "lkr",
    "usd",
    "kwp",
    "mop",
  ], true);

  /** @type {Record<string, import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig>} */
  const fieldsConfig = {
    optTipIndicator: {
      fieldType: "checkbox",
      fieldName: "optTipIndicator",
      label: t(`${FORM_LABEL_KEY}.optTipIndicator`),
      config: {
        customOnCheckFn: (value, { setValue }) => {
          if (!value) {
            setValue("optPayAtTable", false);
            setValue("optTipAdj", false);
          }
        }
      }
    },
    optPayAtTable: {
      fieldType: "checkbox",
      fieldName: "optPayAtTable",
      label: t(`${FORM_LABEL_KEY}.optPayAtTable`),
      disabled: !optTipIndicator,
      config: {
        customOnCheckFn: (value, { setValue }) => {
          value && setValue("optTipAdj", false);
        }
      }
    },
    optTipAdj: {
      fieldType: "checkbox",
      fieldName: "optTipAdj",
      label: t(`${FORM_LABEL_KEY}.optTipAdj`),
      disabled: !optTipIndicator,
      config: {
        customOnCheckFn: (value, { setValue }) => {
          value && setValue("optPayAtTable", false);
        }
      }
    },
    optTips: {
      fieldType: "textInput",
      fieldName: "optTips",
      label: t(`${FORM_LABEL_KEY}.optTips`),
      disabled: !optTipIndicator,
      config: {
        placeholder: t(`${FORM_LABEL_KEY}.optTips.placeholder`),
        rules: ["validTipsOptionFormat", "validTipsOptionLength"],
        onBlurRules: ["validTipsOptionFormatStrict"]
      },
    },
    optCvv: {
      fieldType: "checkbox",
      fieldName: "optCvv",
      label: t(`${FORM_LABEL_KEY}.optCvv`),
    },
    opt_unlink_refund: {
      fieldType: "checkbox",
      fieldName: "opt_unlink_refund",
      label: t(`${FORM_LABEL_KEY}.opt_unlink_refund`),
    },
    currency: {
      fieldType: "selection",
      fieldName: "currency",
      label: t(`${FORM_LABEL_KEY}.currency`),
      mandatory: true,
      config: {
        options: currencyOptions
      }
    },
    tipsCeiling: {
      fieldType: "textInput",
      fieldName: "tipsCeiling",
      label: t(`${FORM_LABEL_KEY}.tipsCeiling`),
      config: {
        rules: ["onlyInteger"]
      }
    },
    optAdminFee: {
      fieldType: "textInput",
      fieldName: "optAdminFee",
      label: t(`${FORM_LABEL_KEY}.optAdminFee`),
      config: {
        rules: ["onlyInteger"]
      }
    },
    maxLimitOfAmount: {
      fieldType: "textInput",
      fieldName: "maxLimitOfAmount",
      label: t(`${FORM_LABEL_KEY}.maxLimitOfAmount`),
      config: {
        rules: ["onlyInteger"]
      }
    },
    optQrcodeScanMode: {
      fieldType: "selection",
      fieldName: "optQrcodeScanMode",
      label: t(`${FORM_LABEL_KEY}.optQrcodeScanMode`),
      mandatory: true,
      config: {
        options: optQrcodeScanModeOptions
      }
    },

    opt_dcc: {
      fieldType: "checkbox",
      fieldName: "opt_dcc",
      label: t(`${FORM_LABEL_KEY}.opt_dcc`),
    },
    opt_dcc_singapore: {
      fieldType: "checkbox",
      fieldName: "opt_dcc_singapore",
      label: t(`${FORM_LABEL_KEY}.opt_dcc_singapore`),
    },
    optDccChc: {
      fieldType: "checkbox",
      fieldName: "optDccChc",
      label: t(`${FORM_LABEL_KEY}.optDccChc`),
    },
    dccCurSelMode: {
      fieldType: "selection",
      fieldName: "dccCurSelMode",
      label: t(`${FORM_LABEL_KEY}.dccCurSelMode`),
      mandatory: true,
      config: {
        options: dccCurSelModeOptions,
      }
    },
    opt_ipp: {
      fieldType: "checkbox",
      fieldName: "opt_ipp",
      label: t(`${FORM_LABEL_KEY}.opt_ipp`),
    },

    sttEnable: {
      fieldType: "checkbox",
      fieldName: "sttEnable",
      label: t(`${FORM_LABEL_KEY}.sttEnable`),
    },
    sttVisa: {
      fieldType: "textInput",
      fieldName: "sttVisa",
      label: t(`${FORM_LABEL_KEY}.sttVisa`),
      mandatory: sttEnable,
      config: {
        rules: ["onlyPositiveNumber"]
      }
    },
    sttMaster: {
      fieldType: "textInput",
      fieldName: "sttMaster",
      label: t(`${FORM_LABEL_KEY}.sttMaster`),
      mandatory: sttEnable,
      config: {
        rules: ["onlyPositiveNumber"]
      }
    },
    sttJcb: {
      fieldType: "textInput",
      fieldName: "sttJcb",
      label: t(`${FORM_LABEL_KEY}.sttJcb`),
      mandatory: sttEnable,
      config: {
        rules: ["onlyPositiveNumber"]
      }
    },
    sttAmex: {
      fieldType: "textInput",
      fieldName: "sttAmex",
      label: t(`${FORM_LABEL_KEY}.sttAmex`),
      mandatory: sttEnable,
      config: {
        rules: ["onlyPositiveNumber"]
      }
    },
    sttCup: {
      fieldType: "textInput",
      fieldName: "sttCup",
      label: t(`${FORM_LABEL_KEY}.sttCup`),
      mandatory: sttEnable,
      config: {
        rules: ["onlyPositiveNumber"]
      }
    },
    sttContactlessVisa: {
      fieldType: "textInput",
      fieldName: "sttContactlessVisa",
      label: t(`${FORM_LABEL_KEY}.sttContactlessVisa`),
      mandatory: sttEnable,
      config: {
        rules: ["onlyPositiveNumber"]
      }
    },
    sttContactlessMaster: {
      fieldType: "textInput",
      fieldName: "sttContactlessMaster",
      label: t(`${FORM_LABEL_KEY}.sttContactlessMaster`),
      mandatory: sttEnable,
      config: {
        rules: ["onlyPositiveNumber"]
      }
    },
    sttContactlessJcb: {
      fieldType: "textInput",
      fieldName: "sttContactlessJcb",
      label: t(`${FORM_LABEL_KEY}.sttContactlessJcb`),
      mandatory: sttEnable,
      config: {
        rules: ["onlyPositiveNumber"]
      }
    },
    sttContactlessAmex: {
      fieldType: "textInput",
      fieldName: "sttContactlessAmex",
      label: t(`${FORM_LABEL_KEY}.sttContactlessAmex`),
      mandatory: sttEnable,
      config: {
        rules: ["onlyPositiveNumber"]
      }
    },
    sttContactlessCup: {
      fieldType: "textInput",
      fieldName: "sttContactlessCup",
      label: t(`${FORM_LABEL_KEY}.sttContactlessCup`),
      mandatory: sttEnable,
      config: {
        rules: ["onlyPositiveNumber"]
      }
    }
  };

  return (
    <FormBuilder.Section
      title={t(`${PAGE_LOCALE_KEY}employeeDetail.config.subtitle.advancedConfig`)}
      titleVariant="h3"
      padding={formBuilderParams.formSectionLeftPadding}>
      <FormBuilder.Section
        title={t(`${PAGE_LOCALE_KEY}employeeDetail.config.subtitle.transaction`)}
        titleVariant="h4">
        <FormBuilder.Row itemsPerRow={3}>
          {FormBuilder.buildFields({
            formProps, fields: [
              fieldsConfig.optTipIndicator,
              fieldsConfig.optPayAtTable,
              fieldsConfig.optTipAdj,
              fieldsConfig.optTips,
              // fieldsConfig.optCvv,
              // fieldsConfig.opt_unlink_refund,
              fieldsConfig.currency,
              fieldsConfig.tipsCeiling,
              // fieldsConfig.optAdminFee,
              fieldsConfig.maxLimitOfAmount,
              fieldsConfig.optQrcodeScanMode
            ]
          })}
        </FormBuilder.Row>
      </FormBuilder.Section>
      {/* <FormBuilder.Section
        title={t(`${PAGE_LOCALE_KEY}employeeDetail.config.subtitle.dccIpp`)}
        titleVariant="h4">
        <FormBuilder.Row itemsPerRow={3}>
          {FormBuilder.buildFields({
            formProps, fields: [
              fieldsConfig.opt_dcc,
              fieldsConfig.opt_dcc_singapore,
              fieldsConfig.optDccChc,
              fieldsConfig.dccCurSelMode,
              fieldsConfig.opt_ipp,
            ]
          })}
        </FormBuilder.Row>
      </FormBuilder.Section> */}
      <FormBuilder.Section
        title={t(`${PAGE_LOCALE_KEY}employeeDetail.config.subtitle.stt`)}
        titleVariant="h4">
        <FormBuilder.Row itemsPerRow={3}>
          {FormBuilder.buildFields({
            formProps, fields: [
              fieldsConfig.sttEnable,
              fieldsConfig.sttVisa,
              fieldsConfig.sttMaster,
              // fieldsConfig.sttJcb,
              // fieldsConfig.sttAmex,
              // fieldsConfig.sttCup,
              fieldsConfig.sttContactlessVisa,
              fieldsConfig.sttContactlessMaster,
              // fieldsConfig.sttContactlessJcb,
              // fieldsConfig.sttContactlessAmex,
              // fieldsConfig.sttContactlessCup,
            ]
          })}
        </FormBuilder.Row>
      </FormBuilder.Section>
    </FormBuilder.Section>
  );
};

export default React.memo(EmployeeAdvancedConfig);
